import { Box, IconButton, InputLabel, TextField } from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import { LabelField } from '@opus/web.core.form.label-field'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { useField, useFormikContext } from 'formik'
import moment from 'moment'
import React, { useCallback, useMemo } from 'react'
import { DATE_ENCRYPT_PATTERN, DATE_FORMAT_SAVE, DATE_FORMAT_VIEW, FIELD_MODE } from '~/common/constants'
import { useFieldError, useFieldFocused } from '~/common/hooks'
import { handleDatePickerModalClick } from '~/common/helpers/focus.helper'
import { CalendarSvg, DeleteSvg } from '~/components/icons'
import { useStyles } from './date-field.style'

export const DateField = ({ name, validate, label, placeholder, mode, saveFormat, onChange, labelStyle, endLabel, viewFormat, ...props }) => {
	const { t } = useTranslation()
	const [field, meta, helper] = useField({
		name,
		validate: mode === FIELD_MODE.edit && validate,
	})
	const { setValue } = helper
	const [focused, focusProps] = useFieldFocused(field)
	const error = useFieldError(meta)
	const classes = useStyles()
	const { setFieldValue } = useFormikContext()

	const handleChange = useCallback(
		(date) => {
			const value = date?.format(saveFormat) || null

			// Store primitive value directly in Formik
			setValue(value)
			setFieldValue(name, value)

			onChange && onChange({ target: { name, value } })
		},
		[field, name, saveFormat, onChange]
	)

	const encrypted = useMemo(() => typeof field.value === 'string' && DATE_ENCRYPT_PATTERN.test(field.value), [field.value])

	const fieldValue = useMemo(() => {
		if (encrypted) return null
		if (!field.value) return null
		const momentDate = moment(field.value, saveFormat)
		return momentDate.isValid() ? momentDate : null
	}, [encrypted, field.value, saveFormat])

	if (mode === FIELD_MODE.view) {
		return <LabelField css={labelStyle} label={t(label)} displayValueFormat={() => (encrypted ? field.value : fieldValue?.format(viewFormat))} />
	}

	const handleClear = (event) => {
		event.stopPropagation() // Prevent the picker from opening
		handleChange(null)
	}

	return (
		<DatePicker
			className={classes.datePicker}
			{...field}
			label={t(label)}
			value={fieldValue}
			{...props}
			mask="" // Prevent the mask from being applied and cause the warning - https://github.com/mui/mui-x/issues/4487#issuecomment-1097731043
			inputFormat={DATE_FORMAT_VIEW}
			toolbarFormat={DATE_FORMAT_VIEW}
			onChange={handleChange}
			openPickerIcon={<CalendarSvg />}
			DialogProps={{
				disableBackdropClick: false,
				disableEscapeKeyDown: false,
				onClick: handleDatePickerModalClick,
			}}
			allowSameDateSelection
			renderInput={(renderProps) => {
				return (
					<>
						<Box display="flex" justifyContent="space-between">
							{label && (
								<InputLabel shrink htmlFor={name} className={classes.label}>
									{t(label)}
								</InputLabel>
							)}
							{endLabel && endLabel}
						</Box>
						<TextField
							{...renderProps}
							helperText={error}
							error={!!error}
							label=""
							fullWidth
							placeholder={t(placeholder)}
							name={name}
							value={fieldValue}
							inputProps={{
								...renderProps.inputProps,
								value: encrypted ? field.value : renderProps?.inputProps.value,
								...focusProps,
								placeholder: t(placeholder),
								'aria-label': t(label),
								'aria-readonly': true,
								readOnly: true,
							}}
							InputLabelProps={{
								shrink: focused || encrypted || !!fieldValue,
								...props.InputLabelProps,
							}}
							InputProps={{
								classes: {
									root: classes.inputRoot,
									input: classes.input,
								},
								endAdornment: (
									<>
										{fieldValue && (
											<IconButton aria-label="clear date" onClick={handleClear} edge="end">
												<DeleteSvg />
											</IconButton>
										)}
										{renderProps.InputProps.endAdornment}
									</>
								),
								...renderProps.InputProps,
							}}
						/>
					</>
				)
			}}
		/>
	)
}

DateField.defaultProps = {
	label: 'SELECT_DATE',
	placeholder: '$PLACEHOLDERS.SELECT_DATE',
	viewFormat: DATE_FORMAT_VIEW,
	saveFormat: DATE_FORMAT_SAVE,
	clearable: true,
	mode: FIELD_MODE.edit,
	minDate: new Date(1700, 0, 1), // Start of year 1700
}
